import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ApiError } from '@/api/client';
import { useVerifyTwoFactorLogin } from '@/api/queries/auth';
import { Button, Field } from '@pixlide/ui';
import { AuthLayout } from '@/features/auth/AuthLayout';

const TWO_FACTOR_ERROR_MESSAGES: Record<string, string> = {
  'auth.two_factor_code_invalid': "Ce code n'est pas valide.",
  'auth.two_factor_challenge_invalid': 'Cette vérification a expiré. Reconnectez-vous.',
};

/**
 * Second temps de la connexion sur un compte 2FA : code de l'application d'authentification,
 * ou l'un des codes de récupération saisi tel quel (l'écran ne fait pas la différence, le
 * serveur tente les deux).
 *
 * Partagé par la connexion classique et la connexion par fournisseur externe, et pas
 * seulement pour éviter une duplication : les deux parcours DOIVENT se comporter à
 * l'identique ici. Un second facteur qui se relâcherait sur l'un des deux chemins annulerait
 * la protection sur les deux.
 */
export function TwoFactorChallengeStep({
  challengeToken,
  onCancel,
}: {
  challengeToken: string;

  /** Absent quand il n'y a nulle part où revenir — au retour d'un fournisseur externe,
   *  le formulaire de départ n'existe plus. */
  onCancel?: () => void;
}) {
  const navigate = useNavigate();
  const verify = useVerifyTwoFactorLogin();
  const [code, setCode] = useState('');

  async function submit() {
    if (code.length === 0) {
      return;
    }

    await verify.mutateAsync({ challengeToken, code });

    // `replace` : l'étape de vérification n'a aucun intérêt dans l'historique, et y revenir
    // par le bouton « précédent » afficherait un jeton de défi désormais consommé.
    await navigate({ to: '/', replace: true });
  }

  const apiError = verify.error instanceof ApiError ? verify.error : null;
  const alertMessage = apiError === null ? null : (TWO_FACTOR_ERROR_MESSAGES[apiError.code] ?? apiError.message);

  return (
    <AuthLayout
      title="Vérification en deux étapes"
      subtitle="Saisissez le code affiché par votre application d'authentification."
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-6"
      >
        <Field
          label="Code de vérification"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          placeholder="123456"
          error={alertMessage ?? undefined}
          value={code}
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, ''));
            verify.reset();
          }}
        />

        <p className="text-[11px] text-ink-500">
          Appareil perdu ? Saisissez plutôt l'un de vos codes de récupération à la place du code.
        </p>

        <div className="flex gap-3">
          {onCancel === undefined ? null : (
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              className="h-11 flex-1 rounded-xl"
            >
              Annuler
            </Button>
          )}

          <Button type="submit" isLoading={verify.isPending} className="h-11 flex-1 rounded-xl">
            Vérifier
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
